/*!
 * BOPF Saved Pieces page hydrator   [v4 - merges with this browser's hearts]
 *
 * v4 (2026-09-23): the Saved page no longer OVERWRITES the hearts kept in this
 * browser (needs server v3.98 or later).
 *   - The server list (the same list the app reads) is MERGED with the local hearts:
 *     server pieces are added here and marked as synced; a local heart that was on
 *     the server but is gone now (removed in the app or another browser) is cleared
 *     ONLY when the server read was complete (degraded:false); a local heart that
 *     never reached the server is kept, shown, and uploaded by the heart script.
 *     v3 replaced the local hearts with the server list and lost unsent saves.
 *   - If the list cannot be loaded, the pieces saved on this device are shown
 *     instead of "You haven't saved anything yet".
 *   - With no email yet, the pieces saved on this device are shown under the email
 *     form (v3 showed only the form).
 *   - A piece whose live check failed (still_available null) is no longer shown as
 *     "No longer here". Only a real sold or unpublished piece is.
 *   - Removing a piece also clears its synced flag, and a remove that fails is
 *     retried on a later page within the hour. A piece saved again elsewhere
 *     AFTER that removal (the server row is newer) is kept, and the old remove
 *     is dropped.
 *   - "change" (sign out) also clears the synced map.
 *
 * Renders the customer's wishlist on /pages/saved.
 *
 * Data flow:
 *   1. If we have an email in localStorage, fetch the canonical list from the
 *      Zara backend with LIVE Shopify prices.
 *   2. Merge it with this browser's hearts (rules above) and render the server
 *      pieces plus any piece saved only on this device, newest first.
 *   3. If we have NO email: show the local pieces (if any) with an email form,
 *      or the empty state with a soft "Have you saved on another device?" link
 *      to email-recall. Recall merges the same way and uploads local pieces.
 *
 * Install:
 *   1. Save as: assets/bopf-saved.js
 *   2. Create Shopify page /pages/saved (title: "Saved Pieces").
 *      In the page body in admin, add ONE line:
 *          <div id="bopf-saved-app"></div>
 *   3. Add to layout/theme.liquid before </body>:
 *          {% if page.handle == 'saved' %}
 *            <script src="{{ 'bopf-saved.js' | asset_url }}?v=4" defer><\/script>
 *          {% endif %}
 */
(function() {
  "use strict";

  var ZARA_BACKEND = "https://bopf-concierge.onrender.com", STORAGE_EMAIL = "bopf_zara_email", STORAGE_SAVED = "bopf_zara_saved", WHATSAPP = "971585669311";

  // v4 (shared with bopf-wishlist-heart.js v12 - keep the names in step)
  var VERSION = "v4",
      STORAGE_SYNCED = "bopf_zara_synced",
      STORAGE_SYNCED_FOR = "bopf_zara_synced_for",
      STORAGE_UNSAVE_PENDING = "bopf_zara_unsave_pending",
      SESSION_HYDRATED = "bopf_zara_hydrated",
      STORAGE_ADD_PENDING = "bopf_zara_add_pending",
      STORAGE_UNSAVED_AT = "bopf_zara_unsaved_at",
      UNSAVE_PENDING_TTL_MS = 36e5,      // a failed remove is retried for up to 1 hour
      UNSAVED_AT_TTL_MS = 6e5,           // un-heart times are kept 10 minutes
      CLOCK_SLACK_MS = 6e4,              // device clock vs server clock margin
      LOCAL_LOOKUP_CAP = 40,
      LOCAL_LOOKUP_TIMEOUT_MS = 5e3,
      NOTE_OFFLINE = "I couldn't reach your full list just now, so these are the pieces saved on this device.";

  function log() {
    try {
      console.log.apply(console, [ "[bopf-saved]" ].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  function lc(s) {
    return String(s == null ? "" : s).trim().toLowerCase();
  }

  function readJSON(key) {
    try {
      var raw = localStorage.getItem(key), v = raw ? JSON.parse(raw) : {};
      return v && typeof v === "object" && !Array.isArray(v) ? v : {};
    } catch (e) {
      return {};
    }
  }

  function writeJSON(key, obj) {
    try {
      localStorage.setItem(key, JSON.stringify(obj || {}));
    } catch (e) {}
  }

  function findKey(map, handle) {
    if (!map || !handle) return null;
    if (Object.prototype.hasOwnProperty.call(map, handle)) return handle;
    var want = lc(handle), keys = Object.keys(map);
    for (var i = 0; i < keys.length; i++) if (lc(keys[i]) === want) return keys[i];
    return null;
  }

  function deleteHandle(map, handle) {
    var hit = false, want = lc(handle);
    Object.keys(map || {}).forEach(function(k) {
      if (k === handle || lc(k) === want) {
        delete map[k];
        hit = true;
      }
    });
    return hit;
  }

  function getEmail() {
    try {
      return localStorage.getItem(STORAGE_EMAIL) || "";
    } catch (e) {
      return "";
    }
  }

  function setEmail(email) {
    try {
      localStorage.setItem(STORAGE_EMAIL, email);
    } catch (e) {}
  }

  function getSavedMap() {
    return readJSON(STORAGE_SAVED);
  }

  function setSavedMap(m, quiet) {
    try {
      localStorage.setItem(STORAGE_SAVED, JSON.stringify(m));
    } catch (e) {}
    try {
      window.dispatchEvent(new CustomEvent("bopf-wishlist-changed", {
        detail: {
          count: Object.keys(m).length,
          quiet: !!quiet
        }
      }));
    } catch (e) {}
  }

  // ---- synced map (shared with the heart script) ----
  function getSyncedOwner() {
    try {
      return localStorage.getItem(STORAGE_SYNCED_FOR) || "";
    } catch (e) {
      return "";
    }
  }

  function getSyncedMap(email) {
    var m = readJSON(STORAGE_SYNCED);
    if (email) {
      var owner = lc(getSyncedOwner()), want = lc(email);
      if (owner && owner !== want) {
        m = {};
        writeJSON(STORAGE_SYNCED, m);
        log("synced flags belonged to another email - cleared");
      }
      if (owner !== want) {
        try {
          localStorage.setItem(STORAGE_SYNCED_FOR, want);
        } catch (e) {}
      }
    }
    return m;
  }

  function setSyncedMap(m) {
    writeJSON(STORAGE_SYNCED, m);
  }

  function resetSyncedFor(email) {
    writeJSON(STORAGE_SYNCED, {});
    try {
      localStorage.setItem(STORAGE_SYNCED_FOR, lc(email));
    } catch (e) {}
  }

  function clearSynced(handle) {
    var m = readJSON(STORAGE_SYNCED);
    if (deleteHandle(m, handle)) setSyncedMap(m);
  }

  // ---- removes that still have to reach the server (retried by the heart script) ----
  function getUnsavePending() {
    var m = readJSON(STORAGE_UNSAVE_PENDING), out = {}, dirty = false, now = Date.now();
    Object.keys(m).forEach(function(k) {
      var v = m[k];
      if (v && typeof v === "object" && v.e && v.t && Math.abs(now - v.t) < UNSAVE_PENDING_TTL_MS) out[k] = v; else dirty = true;
    });
    if (dirty) writeJSON(STORAGE_UNSAVE_PENDING, out);
    return out;
  }

  function addUnsavePending(handle, email) {
    if (!handle || !email) return;
    var m = getUnsavePending();
    m[lc(handle)] = {
      t: Date.now(),
      e: lc(email)
    };
    writeJSON(STORAGE_UNSAVE_PENDING, m);
  }

  function clearUnsavePending(handle) {
    var m = getUnsavePending(), k = lc(handle);
    if (m[k]) {
      delete m[k];
      writeJSON(STORAGE_UNSAVE_PENDING, m);
    }
  }

  // Heart taps still waiting to reach the server (written by the heart script). A
  // removal here cancels the tap.
  function clearAddPending(handle) {
    var m = readJSON(STORAGE_ADD_PENDING), k = lc(handle);
    if (Object.prototype.hasOwnProperty.call(m, k)) {
      delete m[k];
      writeJSON(STORAGE_ADD_PENDING, m);
    }
  }

  // When each piece was last un-hearted in this browser (10 minutes; shared with the
  // heart script). A list read that started before the un-heart is older than it.
  function getUnsavedAt() {
    var m = readJSON(STORAGE_UNSAVED_AT), out = {}, dirty = false, now = Date.now();
    Object.keys(m).forEach(function(k) {
      var t = m[k];
      if (typeof t === "number" && Math.abs(now - t) < UNSAVED_AT_TTL_MS) out[k] = t; else dirty = true;
    });
    if (dirty) writeJSON(STORAGE_UNSAVED_AT, out);
    return out;
  }

  function noteUnsaved(handle) {
    if (!handle) return;
    var m = getUnsavedAt();
    m[lc(handle)] = Date.now();
    writeJSON(STORAGE_UNSAVED_AT, m);
  }

  function stampHydrated(email) {
    try {
      sessionStorage.setItem(SESSION_HYDRATED, JSON.stringify({
        e: lc(email),
        t: Date.now()
      }));
    } catch (e) {}
  }

  function markUnsavedLocal(handle) {
    var m = getSavedMap();
    deleteHandle(m, handle), setSavedMap(m);
    clearSynced(handle);
    clearAddPending(handle);
    noteUnsaved(handle);
  }

  function fmtAed(n) {
    return n == null || isNaN(n) ? "" : "AED " + Math.round(parseFloat(n)).toLocaleString();
  }

  function escapeHtml(s) {
    return String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  // "chanel-classic-flap-bag" -> "Chanel Classic Flap Bag"
  function humanizeHandle(h) {
    var s = String(h || "");
    try {
      s = decodeURIComponent(s);
    } catch (e) {}
    return s.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim().replace(/(^|\s)([a-z])/g, function(all, sp, c) {
      return sp + c.toUpperCase();
    });
  }

  function injectStyles() {
    if (!document.getElementById("bopf-saved-styles")) {
      var s = document.createElement("style");
      s.id = "bopf-saved-styles", s.textContent = '#bopf-saved-app { font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif; color: #1F1A17; max-width: 1280px; margin: 0 auto; padding: 40px 24px 80px; }.bopf-sp-header { text-align: center; margin-bottom: 48px; }.bopf-sp-eyebrow { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: #A0563E; margin: 0 0 12px 0; }.bopf-sp-title { font-family: Georgia, "Times New Roman", serif; font-size: 38px; font-weight: 400; color: #1F1A17; margin: 0 0 12px 0; line-height: 1.2; }.bopf-sp-title em { font-style: italic; color: #A0563E; position: relative; display: inline-block; }.bopf-sp-title em:after { content: ""; position: absolute; left: 0; right: 0; bottom: -6px; height: 8px; background-image: url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 6"><path d="M2 3 Q 14 1, 24 3 T 44 3 T 64 3 T 88 3" stroke="%23A0563E" stroke-width="1.4" fill="none" stroke-linecap="round"/></svg>\');background-repeat: no-repeat; background-size: 100% 100%; }.bopf-sp-subtitle { font-family: Georgia, serif; font-style: italic; font-size: 16px; color: #666; margin: 0; max-width: 480px; margin: 0 auto; line-height: 1.5; }.bopf-sp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 28px 20px; }@media (max-width: 600px) { .bopf-sp-grid { grid-template-columns: repeat(2, 1fr); gap: 18px 12px; } #bopf-saved-app { padding: 24px 16px 60px; } .bopf-sp-title { font-size: 26px; } }.bopf-sp-card { display: flex; flex-direction: column; }.bopf-sp-img-wrap { position: relative; aspect-ratio: 4 / 5; background: #f3eee5; overflow: hidden; }.bopf-sp-img-wrap a { display: block; width: 100%; height: 100%; }.bopf-sp-img-wrap img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s ease; }.bopf-sp-card:hover .bopf-sp-img-wrap img { transform: scale(1.03); }.bopf-sp-remove { position: absolute; top: 10px; right: 10px; width: 30px; height: 30px; background: rgba(255,255,255,0.92); border: none; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; font-size: 18px; color: #1F1A17; transition: background 0.15s, transform 0.1s; z-index: 2; }.bopf-sp-remove:hover { background: rgba(255,255,255,1); color: #A0563E; }.bopf-sp-remove:active { transform: scale(0.92); }.bopf-sp-sold-badge { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(250, 246, 240, 0.85); display: flex; flex-direction: column; align-items: center; justify-content: center; pointer-events: none; padding: 16px; }.bopf-sp-sold-badge .label { color: #1F1A17; font-family: Georgia, serif; font-style: italic; font-size: 16px; letter-spacing: 0.04em; margin: 0 0 4px 0; text-align: center; }.bopf-sp-sold-badge .sub { color: #888; font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; margin: 0; text-align: center; font-family: -apple-system, sans-serif; }.bopf-sp-info { padding: 14px 4px 0 4px; }.bopf-sp-info a { text-decoration: none; color: inherit; }.bopf-sp-info a:hover .bopf-sp-name { text-decoration: underline; }.bopf-sp-name { font-size: 13px; line-height: 1.4; color: #1F1A17; margin: 0 0 6px 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }.bopf-sp-price { font-size: 13px; color: #1F1A17; margin: 0; }.bopf-sp-price-changed { font-size: 11px; color: #A0563E; font-style: italic; margin-top: 3px; }.bopf-sp-price-strike { color: #999; text-decoration: line-through; margin-right: 6px; }.bopf-sp-saved-at { font-size: 11px; color: #999; margin-top: 8px; font-style: italic; font-family: Georgia, serif; }.bopf-sp-wa-link { display: inline-block; margin-top: 8px; font-size: 11px; color: #A0563E; text-decoration: underline; font-style: italic; font-family: Georgia, serif; cursor: pointer; }.bopf-sp-wa-link:hover { color: #8a4732; }.bopf-sp-empty { text-align: center; padding: 60px 20px; max-width: 480px; margin: 0 auto; }.bopf-sp-empty-headline { font-family: Georgia, serif; font-style: italic; font-size: 22px; color: #1F1A17; margin: 0 0 16px 0; line-height: 1.4; }.bopf-sp-empty-body { font-size: 14px; color: #666; line-height: 1.6; margin: 0 0 28px 0; }.bopf-sp-empty-cta { display: inline-block; padding: 14px 32px; background: #1F1A17; color: #FAF6F0; text-decoration: none; font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase; transition: background 0.15s; }.bopf-sp-empty-cta:hover { background: #2c2521; }.bopf-sp-recall-link { display: block; margin-top: 24px; font-size: 12px; color: #A0563E; font-style: italic; font-family: Georgia, serif; cursor: pointer; text-decoration: underline; }.bopf-sp-recall-link:hover { color: #8a4732; }.bopf-sp-recall-form { max-width: 380px; margin: 32px auto 0; padding: 24px; background: #FAF6F0; border-radius: 6px; }.bopf-sp-recall-form label { display: block; font-size: 12px; color: #666; margin-bottom: 8px; letter-spacing: 0.06em; text-transform: uppercase; }.bopf-sp-recall-form input { width: 100%; padding: 12px 14px; font-size: 15px; font-family: inherit; color: #1F1A17; border: 1px solid #d6cfc4; background: #fff; border-radius: 4px; box-sizing: border-box; margin-bottom: 12px; }.bopf-sp-recall-form input:focus { outline: none; border-color: #A0563E; }.bopf-sp-recall-form button { width: 100%; padding: 12px; background: #A0563E; color: #fff; border: none; border-radius: 4px; font-size: 13px; letter-spacing: 0.05em; cursor: pointer; transition: background 0.15s; }.bopf-sp-recall-form button:hover { background: #8a4732; }.bopf-sp-recall-form button:disabled { opacity: 0.5; cursor: not-allowed; }.bopf-sp-recall-error { font-size: 13px; color: #a8443a; margin: 8px 0 0 0; min-height: 18px; }.bopf-sp-loading { text-align: center; padding: 80px 20px; }.bopf-sp-loading-text { font-family: Georgia, serif; font-style: italic; color: #666; font-size: 15px; }.bopf-sp-email-indicator { position: absolute; top: 8px; right: 8px; font-size: 11px; color: #999; font-family: Georgia, serif; font-style: italic; }.bopf-sp-email-indicator a { color: #A0563E; text-decoration: underline; cursor: pointer; }.bopf-sp-share-row { text-align: center; margin: 56px auto 0; max-width: 480px; padding: 0 20px; }.bopf-sp-share-btn { display: inline-block; padding: 12px 28px; background: transparent; color: #1F1A17; text-decoration: none; border: 1px solid #1F1A17; cursor: pointer; font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; transition: background 0.15s, color 0.15s; }.bopf-sp-share-btn:hover { background: #1F1A17; color: #FAF6F0; }.bopf-sp-share-hint { font-family: Georgia, serif; font-style: italic; font-size: 12px; color: #888; margin: 14px 0 0 0; }.bopf-sp-modal-overlay { position: fixed; inset: 0; z-index: 100000; background: rgba(20,15,12,0.55); backdrop-filter: blur(2px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: bopfSpFadeIn 0.18s ease; }@keyframes bopfSpFadeIn { from { opacity: 0; } to { opacity: 1; } }.bopf-sp-modal-card { background: #FAF6F0; border-radius: 8px; width: 100%; max-width: 420px; padding: 30px 28px 26px; box-shadow: 0 20px 60px rgba(20,15,12,.25); position: relative; font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif; color: #1F1A17; }.bopf-sp-modal-x { position: absolute; top: 10px; right: 12px; background: none; border: none; font-size: 24px; line-height: 1; color: #999; cursor: pointer; padding: 6px 10px; border-radius: 50%; }.bopf-sp-modal-x:hover { background: rgba(0,0,0,.05); color: #1F1A17; }.bopf-sp-modal-headline { font-family: Georgia, serif; font-size: 22px; line-height: 1.25; margin: 0 0 12px 0; font-weight: 400; }.bopf-sp-modal-headline em { font-style: italic; color: #A0563E; }.bopf-sp-modal-body { font-size: 14px; line-height: 1.55; color: #3a3530; margin: 0 0 20px 0; }.bopf-sp-modal-primary { display: block; width: 100%; padding: 13px; background: #A0563E; color: #fff; border: none; border-radius: 4px; font-size: 12px; letter-spacing: 0.15em; text-transform: uppercase; cursor: pointer; transition: background 0.15s; margin-bottom: 8px; }.bopf-sp-modal-primary:hover { background: #8a4732; }.bopf-sp-modal-primary:disabled { opacity: 0.6; cursor: not-allowed; }.bopf-sp-modal-cancel { display: block; width: 100%; padding: 10px; background: transparent; color: #666; border: none; cursor: pointer; font-size: 13px; }.bopf-sp-modal-cancel:hover { color: #1F1A17; }.bopf-sp-modal-error { font-size: 13px; color: #a8443a; margin: 10px 0 0 0; min-height: 18px; }.bopf-sp-toast { position: fixed; bottom: 30px; left: 50%; transform: translateX(-50%) translateY(20px); background: #1F1A17; color: #FAF6F0; padding: 14px 22px; border-radius: 4px; font-size: 13px; box-shadow: 0 8px 30px rgba(0,0,0,0.2); z-index: 100001; opacity: 0; transition: opacity 0.25s, transform 0.25s; max-width: 360px; text-align: center; }.bopf-sp-toast.visible { opacity: 1; transform: translateX(-50%) translateY(0); }.bopf-sp-toast--error { background: #8a4732; }.bopf-sp-toast--with-link { padding: 18px 22px; opacity: 1; transform: translateX(-50%) translateY(0); min-width: 320px; text-align: left; padding-right: 50px; }.bopf-sp-toast-title { font-family: Georgia, serif; font-style: italic; font-size: 13px; margin: 0 0 8px 0; color: #FAF6F0; opacity: 0.85; }.bopf-sp-toast-input { width: 100%; padding: 8px 10px; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); color: #FAF6F0; font-size: 12px; border-radius: 3px; box-sizing: border-box; font-family: monospace; }.bopf-sp-toast-close { position: absolute; top: 8px; right: 10px; background: none; border: none; color: rgba(250,246,240,0.7); font-size: 22px; line-height: 1; cursor: pointer; padding: 4px 8px; }.bopf-sp-toast-close:hover { color: #FAF6F0; }' + ".bopf-sp-note { text-align: center; font-family: Georgia, serif; font-style: italic; font-size: 13px; color: #888; max-width: 480px; margin: 0 auto 24px; line-height: 1.5; }.bopf-sp-local { margin-top: 40px; }",
      document.head.appendChild(s);
    }
  }

  function fetchListByEmail(email) {
    return fetch(ZARA_BACKEND + "/zara/wishlist/list?email=" + encodeURIComponent(email)).then(function(r) {
      return r.json();
    });
  }

  // Never rejects. Clears the pending-remove entry once the server took it (or
  // refused it for good); a network error or 5xx/429 leaves it for a retry.
  function postRemove(email, handle) {
    return fetch(ZARA_BACKEND + "/zara/wishlist/remove", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        email: email,
        product_handle: handle
      })
    }).then(function(r) {
      var st = r.status;
      return r.json().catch(function() {
        return {
          ok: !1
        };
      }).then(function(d) {
        d = d && typeof d === "object" ? d : {
          ok: !1
        };
        d._status = st;
        return d;
      });
    }).then(function(d) {
      if (d.ok || d._status >= 400 && d._status < 500 && d._status !== 429) clearUnsavePending(handle);
      return d;
    }, function(err) {
      log("remove failed, will retry on a later page:", handle, err && err.message);
      return {
        ok: !1,
        error: err && err.message
      };
    });
  }

  function renderHeader() {
    return '<div class="bopf-sp-header"><p class="bopf-sp-eyebrow">A note from Hadiza</p><h1 class="bopf-sp-title">Your <em>saved pieces</em></h1><p class="bopf-sp-subtitle">An edit of pieces you\'re keeping an eye on. I\'ll let you know when something changes.</p></div>';
  }

  function renderEmpty(showRecallLink) {
    var html = '<div class="bopf-sp-empty"><p class="bopf-sp-empty-headline">You haven\'t saved anything yet.</p><p class="bopf-sp-empty-body">Click the heart on any piece you\'re keeping an eye on, and I\'ll keep tabs on it for you.</p><a href="/collections/all" class="bopf-sp-empty-cta">Browse pieces</a>';
    return showRecallLink && (html += '<a class="bopf-sp-recall-link" data-bopf-show-recall>Have you saved on another device? Enter your email</a>',
    html += "<div data-bopf-recall-form-slot></div>"), html += "</div>", html;
  }

  function renderRecallForm() {
    return '<form class="bopf-sp-recall-form" data-bopf-recall-form><label for="bopf-sp-recall-email">Your email</label><input type="email" id="bopf-sp-recall-email" placeholder="your@email.com" required autocomplete="email" /><button type="submit">Find my saved pieces</button><p class="bopf-sp-recall-error" data-bopf-recall-error></p></form>';
  }

  function renderEmailIndicator(email) {
    return '<div class="bopf-sp-email-indicator">Showing pieces saved by ' + escapeHtml(email) + " &middot; <a data-bopf-change-email>change</a></div>";
  }

  // Sold ("No longer here") only when the server says so: still_available === false
  // (a real 404 - sold or unpublished). A failed live check comes back as
  // still_available null (server v3.98) and shows the saved snapshot as a normal card.
  function isSoldItem(item) {
    return !!item && item.still_available === !1;
  }

  function renderCard(item) {
    var displayTitle = item.live && item.live.title || item.stored_title || "Untitled", displayImage = item.live && item.live.image || item.stored_image || "", displayUrl = "/products/" + item.product_handle, sold = isSoldItem(item), livePrice = item.live ? item.live.price_aed : null, storedPrice = item.stored_price_aed, priceChanged = item.price_changed, priceHtml = "";
    livePrice !== null ? priceChanged && storedPrice !== null && livePrice < storedPrice ? priceHtml = '<span class="bopf-sp-price-strike">' + fmtAed(storedPrice) + "</span>" + fmtAed(livePrice) : priceHtml = fmtAed(livePrice) : storedPrice !== null && (priceHtml = fmtAed(storedPrice));
    var savedAtHtml = "";
    if (item.saved_at) {
      var d = new Date(item.saved_at);
      if (!isNaN(d.getTime())) {
        var days = Math.floor((Date.now() - d.getTime()) / 864e5), savedLabel = "";
        days === 0 ? savedLabel = "Saved today" : days === 1 ? savedLabel = "Saved yesterday" : days < 7 ? savedLabel = "Saved " + days + " days ago" : days < 30 ? savedLabel = "Saved " + Math.floor(days / 7) + " week" + (days < 14 ? "" : "s") + " ago" : savedLabel = "Saved " + Math.floor(days / 30) + " month" + (days < 60 ? "" : "s") + " ago",
        savedAtHtml = '<p class="bopf-sp-saved-at">' + savedLabel + "</p>";
      }
    }
    var waText = encodeURIComponent("Hi Hadiza! I'm interested in " + displayTitle + " (" + window.location.origin + displayUrl + "). Could you tell me more about it?"), waUrl = "https://wa.me/" + WHATSAPP + "?text=" + waText;
    return '<div class="bopf-sp-card" data-handle="' + escapeHtml(item.product_handle) + '"><div class="bopf-sp-img-wrap"><a href="' + escapeHtml(displayUrl) + '">' + (displayImage ? '<img src="' + escapeHtml(displayImage) + '" alt="' + escapeHtml(displayTitle) + '" loading="lazy" />' : "") + '</a><button class="bopf-sp-remove" aria-label="Remove from saved" data-bopf-remove="' + escapeHtml(item.product_handle) + '">&times;</button>' + (sold ? '<div class="bopf-sp-sold-badge"><p class="label">No longer here</p><p class="sub">Tap \xD7 to remove</p></div>' : "") + '</div><div class="bopf-sp-info"><a href="' + escapeHtml(displayUrl) + '"><p class="bopf-sp-name">' + escapeHtml(displayTitle) + "</p></a>" + (priceHtml ? '<p class="bopf-sp-price">' + priceHtml + "</p>" : "") + (priceChanged && storedPrice && livePrice && livePrice < storedPrice ? '<p class="bopf-sp-price-changed">Price came down since you saved</p>' : "") + savedAtHtml + '<a href="' + escapeHtml(waUrl) + '" target="_blank" rel="noopener" class="bopf-sp-wa-link">Ask Hadiza about this piece</a></div></div>';
  }

  function renderGrid(items, email, note) {
    if (!items.length) return renderEmpty(!0);
    var emailIndicator = email ? renderEmailIndicator(email) : "", noteHtml = note ? '<p class="bopf-sp-note">' + escapeHtml(note) + "</p>" : "";
    return emailIndicator + noteHtml + '<div class="bopf-sp-grid">' + items.map(renderCard).join("") + "</div>";
  }

  // onEmpty (optional): what to do when the last card is removed (default: empty state).
  function wireActions(root, onEmpty) {
    root.querySelectorAll("[data-bopf-remove]").forEach(function(btn) {
      if (btn._bopfWired) return;
      btn._bopfWired = !0;
      btn.addEventListener("click", function(e) {
        e.preventDefault();
        var handle = btn.getAttribute("data-bopf-remove"), card = btn.closest(".bopf-sp-card");
        if (card) {
          card.style.transition = "opacity 0.2s, transform 0.2s", card.style.opacity = "0",
          card.style.transform = "scale(0.95)", setTimeout(function() {
            card.parentNode && card.parentNode.removeChild(card);
            var grid = root.querySelector(".bopf-sp-grid");
            if (grid && grid.children.length === 0) {
              if (onEmpty) {
                onEmpty();
                return;
              }
              grid.outerHTML = renderEmpty(!0), wireActions(root);
            }
          }, 220), markUnsavedLocal(handle);
          var email = getEmail();
          if (email) {
            addUnsavePending(handle, email);
            postRemove(email, handle).then(function(d) {
              log("remove backend:", d);
            });
          }
        }
      });
    });
    var changeBtn = root.querySelector("[data-bopf-change-email]");
    changeBtn && !changeBtn._bopfWired && (changeBtn._bopfWired = !0, changeBtn.addEventListener("click", function(e) {
      if (e.preventDefault(), !!confirm("Sign out and clear your saved pieces from this device? You can recall them again with your email.")) {
        try {
          localStorage.removeItem(STORAGE_EMAIL), localStorage.removeItem(STORAGE_SAVED);
        } catch (e2) {}
        // v4: the synced flags belonged to that email - clear them too.
        try {
          localStorage.removeItem(STORAGE_SYNCED), localStorage.removeItem(STORAGE_SYNCED_FOR);
        } catch (e3) {}
        try {
          sessionStorage.removeItem(SESSION_HYDRATED);
        } catch (e4) {}
        try {
          window.dispatchEvent(new CustomEvent("bopf-wishlist-changed", {
            detail: {
              count: 0,
              quiet: !0
            }
          }));
        } catch (e5) {}
        boot();
      }
    }));
    var showRecallBtn = root.querySelector("[data-bopf-show-recall]");
    showRecallBtn && !showRecallBtn._bopfWired && (showRecallBtn._bopfWired = !0, showRecallBtn.addEventListener("click", function(e) {
      e.preventDefault();
      var slot = root.querySelector("[data-bopf-recall-form-slot]");
      if (slot) {
        slot.innerHTML = renderRecallForm(), wireRecallForm(root);
        var input = slot.querySelector("input");
        input && setTimeout(function() {
          input.focus();
        }, 50);
      }
      showRecallBtn.style.display = "none";
    }));
  }

  function wireRecallForm(root) {
    var form = root.querySelector("[data-bopf-recall-form]");
    form && form.addEventListener("submit", function(e) {
      e.preventDefault();
      var input = form.querySelector('input[type="email"]'), errEl = form.querySelector("[data-bopf-recall-error]"), btn = form.querySelector("button"), email = (input.value || "").trim().toLowerCase();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errEl.textContent = "Please enter a valid email.";
        return;
      }
      errEl.textContent = "", btn.disabled = !0, btn.textContent = "Looking up...";
      var startedAt = Date.now();
      fetchListByEmail(email).then(function(d) {
        if (d.ok) {
          // v4: MERGE (v3 replaced the local hearts). Synced flags recorded for a
          // different email mean nothing for this one.
          if (lc(getEmail()) !== email && lc(getSyncedOwner()) !== email) resetSyncedFor(email);
          setEmail(email);
          var mr = mergeServerList(d, email, startedAt);
          stampHydrated(email);
          return showMerged(mr.items, mr.localOnly, email, null).then(function() {
            // Send the pieces saved only on this device to her list now.
            triggerHeartFlush(email);
          });
        } else errEl.textContent = d.error || "Could not retrieve your saved pieces.", btn.disabled = !1,
        btn.textContent = "Find my saved pieces";
      }).catch(function(err) {
        log("recall error:", err), errEl.textContent = "Connection error. Please try again.",
        btn.disabled = !1, btn.textContent = "Find my saved pieces";
      });
    });
  }

  function wireShareButton(root, email) {
    var btn = root.querySelector("[data-bopf-share]");
    btn && btn.addEventListener("click", function(e) {
      if (e.preventDefault(), !email) {
        showShareToast("We need your email to create a share link. Try saving a piece first.", !0);
        return;
      }
      openShareConfirmModal(email);
    });
  }

  function openShareConfirmModal(email) {
    var existing = document.getElementById("bopf-share-modal");
    existing && existing.remove();
    var modal = document.createElement("div");
    modal.id = "bopf-share-modal", modal.className = "bopf-sp-modal-overlay", modal.innerHTML = '<div class="bopf-sp-modal-card"><button class="bopf-sp-modal-x" aria-label="Close">&times;</button><p class="bopf-sp-eyebrow">A shareable link</p><h2 class="bopf-sp-modal-headline">Share your <em>edit</em></h2><p class="bopf-sp-modal-body">I\'ll generate a private link to your saved pieces. Anyone with the link can view your edit for 30 days. Your email won\'t be visible.</p><button class="bopf-sp-modal-primary" data-bopf-share-confirm>Create my link</button><button class="bopf-sp-modal-cancel" data-bopf-share-cancel>Not now</button><p class="bopf-sp-modal-error" data-bopf-share-error></p></div>',
    document.body.appendChild(modal), setTimeout(function() {
      var primary = modal.querySelector(".bopf-sp-modal-primary");
      primary && primary.focus();
    }, 50);
    function close() {
      modal.parentNode && modal.parentNode.removeChild(modal);
    }
    modal.querySelector(".bopf-sp-modal-x").addEventListener("click", close), modal.querySelector("[data-bopf-share-cancel]").addEventListener("click", close),
    modal.addEventListener("click", function(e) {
      e.target === modal && close();
    }), document.addEventListener("keydown", function escHandler(e) {
      e.key === "Escape" && (close(), document.removeEventListener("keydown", escHandler));
    });
    var confirmBtn = modal.querySelector("[data-bopf-share-confirm]"), errEl = modal.querySelector("[data-bopf-share-error]");
    confirmBtn.addEventListener("click", function() {
      confirmBtn.disabled = !0, confirmBtn.textContent = "Creating link...", errEl.textContent = "",
      postCreateShare(email).then(function(d) {
        if (!d.ok) {
          errEl.textContent = d.error || "Could not create link. Please try again.", confirmBtn.disabled = !1,
          confirmBtn.textContent = "Create my link";
          return;
        }
        close(), deliverShareLink(d.url);
      }).catch(function(err) {
        log("share create error:", err), errEl.textContent = "Connection error. Please try again.",
        confirmBtn.disabled = !1, confirmBtn.textContent = "Create my link";
      });
    });
  }

  function deliverShareLink(url) {
    var canNativeShare = !!(navigator.share && navigator.canShare && navigator.canShare({
      url: url
    }));
    canNativeShare ? navigator.share({
      title: "My BOPF saved pieces",
      text: "A few pieces I'm keeping an eye on at BOPF",
      url: url
    }).then(function() {
      showShareToast("Link shared.");
    }).catch(function() {
      copyToClipboard(url);
    }) : copyToClipboard(url);
  }

  function copyToClipboard(text) {
    navigator.clipboard && navigator.clipboard.writeText ? navigator.clipboard.writeText(text).then(function() {
      showShareToast("Link copied. Paste it anywhere to share.");
    }).catch(function() {
      showShareToastWithLink(text);
    }) : showShareToastWithLink(text);
  }

  function showShareToastWithLink(url) {
    var existing = document.getElementById("bopf-share-toast");
    existing && existing.remove();
    var toast = document.createElement("div");
    toast.id = "bopf-share-toast", toast.className = "bopf-sp-toast bopf-sp-toast--with-link",
    toast.innerHTML = '<p class="bopf-sp-toast-title">Your share link</p><input type="text" readonly value="' + escapeHtml(url) + '" class="bopf-sp-toast-input" /><button class="bopf-sp-toast-close" aria-label="Close">&times;</button>',
    document.body.appendChild(toast);
    var input = toast.querySelector("input");
    setTimeout(function() {
      input.focus(), input.select();
    }, 50), toast.querySelector(".bopf-sp-toast-close").addEventListener("click", function() {
      toast.remove();
    }), setTimeout(function() {
      toast.parentNode && toast.remove();
    }, 12e3);
  }

  function showShareToast(message, isError) {
    var existing = document.getElementById("bopf-share-toast");
    existing && existing.remove();
    var toast = document.createElement("div");
    toast.id = "bopf-share-toast", toast.className = "bopf-sp-toast" + (isError ? " bopf-sp-toast--error" : ""),
    toast.textContent = message, document.body.appendChild(toast), setTimeout(function() {
      toast.classList.add("visible");
    }, 10), setTimeout(function() {
      toast.classList.remove("visible"), setTimeout(function() {
        toast.parentNode && toast.remove();
      }, 300);
    }, 4500);
  }

  function renderApp(items, email, note) {
    var root = document.getElementById("bopf-saved-app");
    if (root) {
      var shareButtonHtml = items && items.length ? renderShareButton() : "";
      root.innerHTML = renderHeader() + renderGrid(items || [], email, note) + shareButtonHtml,
      wireActions(root), wireShareButton(root, email);
    }
  }

  function renderShareButton() {
    return '<div class="bopf-sp-share-row"><a class="bopf-sp-share-btn" data-bopf-share>Share my saved pieces</a><p class="bopf-sp-share-hint">Send a 30-day link to a friend. Your email stays private.</p></div>';
  }

  function renderSharedApp(items, expiresAt) {
    var root = document.getElementById("bopf-saved-app");
    if (root) {
      var headerHtml = '<div class="bopf-sp-header"><p class="bopf-sp-eyebrow">A shared edit</p><h1 class="bopf-sp-title">A friend\'s <em>saved pieces</em></h1><p class="bopf-sp-subtitle">Curated picks from a BOPF customer. Tap any piece to see it on our storefront.</p></div>';
      if (!items.length) {
        root.innerHTML = headerHtml + '<div class="bopf-sp-empty"><p class="bopf-sp-empty-headline">This wishlist is empty.</p><a href="/collections/all" class="bopf-sp-empty-cta">Browse pieces</a></div>';
        return;
      }
      var grid = '<div class="bopf-sp-grid">' + items.map(function(it) {
        return renderSharedCard(it);
      }).join("") + "</div>", ctaHtml = '<div class="bopf-sp-share-row"><a href="/collections/all" class="bopf-sp-share-btn">Discover your own pieces</a><p class="bopf-sp-share-hint">Like what you see? Build your own edit.</p></div>';
      root.innerHTML = headerHtml + grid + ctaHtml;
    }
  }

  function renderSharedCard(item) {
    var displayTitle = item.live && item.live.title || item.stored_title || "Untitled", displayImage = item.live && item.live.image || item.stored_image || "", displayUrl = "/products/" + item.product_handle, sold = isSoldItem(item), livePrice = item.live ? item.live.price_aed : null, priceHtml = livePrice !== null ? fmtAed(livePrice) : "";
    return '<div class="bopf-sp-card"><div class="bopf-sp-img-wrap"><a href="' + escapeHtml(displayUrl) + '">' + (displayImage ? '<img src="' + escapeHtml(displayImage) + '" alt="' + escapeHtml(displayTitle) + '" loading="lazy" />' : "") + "</a>" + (sold ? '<div class="bopf-sp-sold-badge"><p class="label">No longer here</p></div>' : "") + '</div><div class="bopf-sp-info"><a href="' + escapeHtml(displayUrl) + '"><p class="bopf-sp-name">' + escapeHtml(displayTitle) + "</p></a>" + (priceHtml ? '<p class="bopf-sp-price">' + priceHtml + "</p>" : "") + "</div></div>";
  }

  function renderShareError(reason, statusCode) {
    var root = document.getElementById("bopf-saved-app");
    if (root) {
      var headline, body;
      reason === "expired" ? (headline = "This share link has expired.", body = "Share links last 30 days. Ask the sender for a fresh one, or browse our latest pieces.") : reason === "revoked" ? (headline = "This share link has been revoked.",
      body = "The owner of this wishlist has chosen to stop sharing it.") : reason === "connection" ? (headline = "Connection trouble.",
      body = "We couldn't load this shared wishlist. Please try again in a moment.") : (headline = "This share link isn't valid.",
      body = "The link may be mistyped, or it may belong to a wishlist that no longer exists."),
      root.innerHTML = '<div class="bopf-sp-header"><p class="bopf-sp-eyebrow">A shared edit</p><h1 class="bopf-sp-title">Saved <em>pieces</em></h1></div><div class="bopf-sp-empty"><p class="bopf-sp-empty-headline">' + escapeHtml(headline) + '</p><p class="bopf-sp-empty-body">' + escapeHtml(body) + '</p><a href="/collections/all" class="bopf-sp-empty-cta">Browse pieces</a></div>';
    }
  }

  function renderLoading(reason) {
    var root = document.getElementById("bopf-saved-app");
    root && (root.innerHTML = renderHeader() + '<div class="bopf-sp-loading"><p class="bopf-sp-loading-text">' + escapeHtml(reason || "Just a moment...") + "</p></div>");
  }

  function getShareToken() {
    try {
      var u = new URL(window.location.href), t = (u.searchParams.get("share") || "").trim();
      return /^[a-z0-9]{16,32}$/.test(t) ? t : null;
    } catch (e) {
      return null;
    }
  }

  function fetchSharedList(token) {
    return fetch(ZARA_BACKEND + "/zara/wishlist/share/view?token=" + encodeURIComponent(token)).then(function(r) {
      return r.json().then(function(d) {
        return d._status = r.status, d;
      });
    });
  }

  function postCreateShare(email) {
    return fetch(ZARA_BACKEND + "/zara/wishlist/share/create", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        email: email
      })
    }).then(function(r) {
      return r.json();
    });
  }

  // ---- v4: merge + pieces saved only on this device ----
  function tsOf(v) {
    var t = typeof v === "number" ? v : Date.parse(v || "");
    return isFinite(t) && t > 0 ? t : 0;
  }

  // The same merge rules as the heart script (bopf-wishlist-heart.js v12):
  //  - a piece on the server -> hearted here and marked synced (unless a remove
  //    of it is still waiting to reach the server: then it is hidden and re-sent,
  //    or the server row is newer than that remove: then the remove is dropped;
  //    or it was un-hearted in this browser after the read started: hidden)
  //  - a local heart marked synced but absent on the server -> cleared, ONLY when
  //    degraded is false and it was not saved/confirmed after the read started
  //  - anything else local -> kept and shown as a "this device" card
  function mergeServerList(d, email, startedAt) {
    var degraded = d.degraded !== !1, items = Array.isArray(d.items) ? d.items : [], saved = getSavedMap(), synced = getSyncedMap(email), pending = getUnsavePending(), unsavedAt = getUnsavedAt(), want = lc(email), now = Date.now(), seen = {}, serverItems = [], retry = [], removed = 0;
    items.forEach(function(it) {
      if (!it || !it.product_handle) return;
      var hl = lc(it.product_handle);
      if (!hl || seen[hl]) return;
      seen[hl] = 1;
      var pe = pending[hl];
      if (pe && pe.e === want) {
        var rowT = tsOf(it.saved_at);
        if (rowT && rowT > pe.t + CLOCK_SLACK_MS) {
          // Saved again elsewhere AFTER this browser's un-heart: that newer save wins.
          clearUnsavePending(hl);
          log("remove dropped (saved again since):", it.product_handle);
        } else {
          retry.push(it.product_handle);
          return;
        }
      }
      // Un-hearted in this browser after this read started: the read is out of date.
      if (unsavedAt[hl] && unsavedAt[hl] >= startedAt) return;
      serverItems.push(it);
      var k = findKey(saved, hl);
      if (!k) {
        k = hl;
        var t = tsOf(it.saved_at);
        saved[k] = t || now;
      } else if (!saved[k]) saved[k] = tsOf(it.saved_at) || now;
      synced[k] = synced[k] || now;
    });
    var localOnly = [];
    Object.keys(saved).forEach(function(k) {
      if (seen[lc(k)]) return;
      var st = synced[k] || synced[lc(k)];
      if (st && !degraded && st < startedAt && !(saved[k] && saved[k] >= startedAt)) {
        delete saved[k];
        removed++;
        return;
      }
      localOnly.push(k);
    });
    Object.keys(synced).forEach(function(k) {
      if (!findKey(saved, k)) delete synced[k];
    });
    setSyncedMap(synced);
    setSavedMap(saved);
    retry.forEach(function(h) {
      postRemove(email, h).then(function(r) {
        log("remove retry:", h, r);
      });
    });
    log("merged:", {
      server: serverItems.length,
      local_only: localOnly.length,
      cleared: removed,
      degraded: degraded
    });
    return {
      items: serverItems,
      localOnly: localOnly,
      degraded: degraded
    };
  }

  function withTimeout(p, ms, fallback) {
    return new Promise(function(resolve) {
      var done = !1;
      setTimeout(function() {
        done || (done = !0, resolve(fallback));
      }, ms);
      p.then(function(v) {
        done || (done = !0, resolve(v));
      }, function() {
        done || (done = !0, resolve(fallback));
      });
    });
  }

  // Card data for pieces saved only on this device (same shape as a /list item).
  function lookupLocal(handles, savedMap) {
    return Promise.all(handles.map(function(h, i) {
      var ts = tsOf(savedMap[h]), savedAt = null;
      try {
        savedAt = ts ? new Date(ts).toISOString() : null;
      } catch (e) {}
      var base = {
        product_handle: h,
        saved_at: savedAt,
        stored_title: humanizeHandle(h),
        stored_image: "",
        stored_price_aed: null,
        stored_url: "/products/" + h,
        live: null,
        price_changed: !1,
        still_available: null,
        local_only: !0
      };
      if (i >= LOCAL_LOOKUP_CAP) return base;
      var lookup = fetch("/products/" + encodeURIComponent(h) + ".js").then(function(r) {
        if (r.status === 404) {
          base.live = {
            title: base.stored_title,
            price_aed: null,
            available: !1,
            image: ""
          };
          base.still_available = !1;
          return base;
        }
        if (!r.ok) return base;
        return r.json().then(function(p) {
          var img = p && p.featured_image ? String(p.featured_image).indexOf("//") === 0 ? "https:" + p.featured_image : String(p.featured_image) : "";
          base.stored_title = p && p.title || base.stored_title;
          base.stored_image = img;
          base.live = {
            title: base.stored_title,
            price_aed: null,
            available: !0,
            image: img
          };
          base.still_available = !0;
          return base;
        });
      });
      return withTimeout(lookup, LOCAL_LOOKUP_TIMEOUT_MS, base);
    }));
  }

  // Server items keep their order; each local piece goes in before the first server
  // piece saved earlier than it (newest first overall).
  function insertByTime(serverItems, localItems) {
    var out = serverItems.slice(), locals = localItems.slice().sort(function(a, b) {
      return tsOf(b.saved_at) - tsOf(a.saved_at);
    });
    locals.forEach(function(it) {
      var t = tsOf(it.saved_at), at = out.length;
      for (var i = 0; i < out.length; i++) {
        var st = tsOf(out[i].saved_at);
        if (st && t > st) {
          at = i;
          break;
        }
      }
      out.splice(at, 0, it);
    });
    return out;
  }

  function showMerged(serverItems, localHandles, email, note) {
    if (!localHandles.length) {
      renderApp(serverItems, email, note);
      return Promise.resolve();
    }
    return lookupLocal(localHandles, getSavedMap()).then(function(localItems) {
      if (lc(getEmail()) !== lc(email)) return;
      var now = getSavedMap();
      localItems = localItems.filter(function(it) {
        return !!findKey(now, it.product_handle);
      });
      renderApp(insertByTime(serverItems, localItems), email, note);
    });
  }

  // The list could not be loaded: show this device's pieces rather than "nothing saved".
  function showLocalFallback(email) {
    var saved = getSavedMap(), hs = Object.keys(saved).filter(Boolean);
    if (!hs.length) {
      renderApp([], email);
      return Promise.resolve();
    }
    return lookupLocal(hs, saved).then(function(items) {
      if (lc(getEmail()) !== lc(email)) return;
      renderApp(insertByTime([], items), email, NOTE_OFFLINE);
    });
  }

  function triggerHeartFlush(email) {
    try {
      var h = window.bopfWishlistHeart;
      h && typeof h.flush === "function" && h.flush(email);
    } catch (e) {}
  }

  function boot() {
    injectStyles();
    var root = document.getElementById("bopf-saved-app");
    if (!root) {
      log("no #bopf-saved-app element found on page");
      return;
    }
    var shareToken = getShareToken();
    if (shareToken) {
      log("share-view mode, token=" + shareToken), renderLoading("Loading shared wishlist..."),
      fetchSharedList(shareToken).then(function(d) {
        d.ok ? renderSharedApp(d.items || [], d.expires_at) : renderShareError(d.error || "invalid", d._status);
      }).catch(function(err) {
        log("share fetch failed:", err), renderShareError("connection");
      });
      return;
    }
    var email = getEmail(), savedMap = getSavedMap(), hasLocalItems = Object.keys(savedMap).length > 0;
    if (!email && !hasLocalItems) {
      log("no email, no local items"), root.innerHTML = renderHeader() + renderEmpty(!0),
      wireActions(root);
      return;
    }
    if (email) {
      log("fetching for", email), renderLoading("Loading your saved pieces...");
      var startedAt = Date.now();
      var merging = fetchListByEmail(email).then(function(d) {
        if (d && d.ok) {
          var mr = mergeServerList(d, email, startedAt);
          stampHydrated(email);
          return showMerged(mr.items, mr.localOnly, email, null);
        }
        log("list error:", d && d.error);
        return showLocalFallback(email);
      }).catch(function(err) {
        log("fetch failed:", err);
        return showLocalFallback(email);
      });
      // The heart script's 2.5s flush waits for this merge (so it does not re-send
      // pieces the server already holds).
      try {
        window.bopfWishlistMerging = merging;
      } catch (e) {}
      merging.then(function() {
        try {
          window.bopfWishlistMerging === merging && (window.bopfWishlistMerging = null);
        } catch (e) {}
      });
      return;
    }
    log("have local items but no email"), root.innerHTML = renderHeader() + '<div class="bopf-sp-empty"><p class="bopf-sp-empty-headline">Your saved pieces live on this device.</p><p class="bopf-sp-empty-body">Add your email so I can keep them safe across devices and let you know when prices change.</p>' + renderRecallForm() + "</div>" + '<div class="bopf-sp-local" data-bopf-local-slot></div>',
    wireRecallForm(root);
    // v4: show the pieces saved on this device under the form.
    lookupLocal(Object.keys(savedMap).filter(Boolean), savedMap).then(function(items) {
      var slot = root.querySelector("[data-bopf-local-slot]");
      if (!slot || !items.length || getEmail()) return;
      slot.innerHTML = '<div class="bopf-sp-grid">' + insertByTime([], items).map(renderCard).join("") + "</div>";
      wireActions(slot, function() {
        boot();
      });
    });
  }

  log(VERSION + " script parsed, readyState =", document.readyState), document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", boot) : boot();
})();
